import {ChangeDetectionStrategy, Component, computed, effect, inject, signal} from '@angular/core';
import {Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {DirectoryStore} from '../../services/directory-store';
import {Loader} from '../../../../components/loader/loader';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Search} from '../../../../components/search/search';
import {FilterContainer} from '../../../../components/filter-container/filter-container';
import {RouteUri} from '../../../../constants/route-uri';
import {ConfirmService} from '../../../../services/confirm.service';
import {CompanySummary, CustomerState} from '../../../../../api';
import {CompanyGrid} from '../company-grid/company-grid';
import {CustomerStateDropdown} from '../customer-state-dropdown/customer-state-dropdown';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'elcia-directory-companies',
  imports: [
    Loader,
    ElciaButton,
    Search,
    FilterContainer,
    CompanyGrid,
    CustomerStateDropdown,
    TranslatePipe,
  ],
  templateUrl: './directory-companies.html',
  styleUrl: './directory-companies.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DirectoryCompanies {
  private readonly directoryStore = inject(DirectoryStore);
  private readonly router = inject(Router);
  private readonly confirmService = inject(ConfirmService);

  protected readonly companies = computed(() => this.directoryStore.companySummaries());
  protected readonly isLoading = computed(() => this.directoryStore.isDataLoading());

  protected readonly isFilterDisplayed = signal(false);

  protected readonly searchControl = new FormControl('', {nonNullable: true});
  protected readonly statesControl = new FormControl<CustomerState[]>([CustomerState.IsActive], {nonNullable: true});

  private readonly searchValue = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.searchControl.value}
  );

  private readonly statesValue = toSignal(
    this.statesControl.valueChanges,
    {initialValue: this.statesControl.value}
  );

  private readonly syncSearch = effect(() => {
    this.directoryStore.companySearchTerm.set(this.searchValue() || null);
  });

  private readonly syncStates = effect(() => {
    this.directoryStore.companyStates.set(this.statesValue());
  });

  protected toggleFilterDisplayed(): void {
    this.isFilterDisplayed.update(v => !v);
  }

  constructor() {
    this.directoryStore.refreshIfStale();
  }

  protected async navToForm() {
    await this.router.navigate(['/' + RouteUri.DIRECTORY_FORM, RouteUri.DIRECTORY_FORM_COMPANY]);
  }

  protected async onCompanySelected(company: CompanySummary) {
    await this.router.navigate(['/' + RouteUri.DIRECTORY_FORM, company.id]);
  }

  protected onCompanyDelete(company: CompanySummary): void {
    this.confirmService.confirm({
      title: 'entity.company',
      message: company.name,
      accept: async () => {
        await this.directoryStore.deleteCompanyMutation.mutateAsync(company.id);
      }
    });
  }
}
